import { useState, useEffect, useCallback } from "react";
import {
  getPublicBrowsableChallenges,
  getUserChallenges,
  joinChallenge,
  leaveChallenge,
  getChallengeLeaderboard,
  addManualEntry,
  updateChallengeProgressPrefs,
  finalizeEndedChallenges,
} from "./challenges";

const typeEmoji = { count: "🏆", race: "🏁", collective: "🤝" };
const typeLabel = { count: "Most Wins", race: "Race", collective: "Collective" };
const statusColor = { active: "#16a34a", upcoming: "#d97706", completed: "#6b7280" };

export default function ChallengesScreen({ currentUser }) {
  const [subTab, setSubTab] = useState("mine");
  const [myChallenges, setMyChallenges] = useState([]);
  const [browseChallenges, setBrowseChallenges] = useState([]);
  const [leaderboards, setLeaderboards] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);
  const [leavingId, setLeavingId] = useState(null);
  const [manualEntry, setManualEntry] = useState(null); // { challengeId, value, note }
  const [submittingEntry, setSubmittingEntry] = useState(false);

  const { uid, name, avatar } = currentUser;

  const load = useCallback(async () => {
    setLoading(true);
    // Finalize any challenges whose end date has passed before reading the list.
    await finalizeEndedChallenges(uid);
    const [mine, browse] = await Promise.all([
      getUserChallenges(uid),
      getPublicBrowsableChallenges(uid),
    ]);
    setMyChallenges(mine);
    setBrowseChallenges(browse);
    setLoading(false);
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  async function loadLeaderboard(challengeId) {
    if (leaderboards[challengeId]) return;
    const lb = await getChallengeLeaderboard(challengeId);
    setLeaderboards((prev) => ({ ...prev, [challengeId]: lb }));
  }

  async function handleExpand(challengeId) {
    if (expandedId === challengeId) { setExpandedId(null); return; }
    setExpandedId(challengeId);
    await loadLeaderboard(challengeId);
  }

  async function handleJoin(challenge) {
    setJoiningId(challenge.id);
    try {
      await joinChallenge(challenge.id, uid, name, avatar);
      await load();
      setSubTab("mine");
    } finally {
      setJoiningId(null);
    }
  }

  async function handleLeave(challengeId) {
    setLeavingId(challengeId);
    try {
      await leaveChallenge(challengeId, uid);
      await load();
    } finally {
      setLeavingId(null);
    }
  }

  async function handleManualSubmit(challengeId) {
    if (!manualEntry || submittingEntry) return;
    setSubmittingEntry(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await addManualEntry(challengeId, uid, today, manualEntry.value || 1, manualEntry.note || "");
      setManualEntry(null);
      // Refresh leaderboard
      const lb = await getChallengeLeaderboard(challengeId);
      setLeaderboards((prev) => ({ ...prev, [challengeId]: lb }));
      await load();
    } finally {
      setSubmittingEntry(false);
    }
  }

  if (loading) {
    return <div style={{ color: "#4b6b8a", padding: "20px", textAlign: "center" }}>Loading challenges…</div>;
  }

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "18px" }}>
        {[["mine", "My Challenges"], ["browse", "Browse"]].map(([key, label]) => (
          <button key={key} onClick={() => setSubTab(key)}
            style={{
              padding: "8px 18px", borderRadius: "20px",
              border: subTab === key ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: subTab === key ? "#2563eb" : "#f5faff",
              color: subTab === key ? "#fff" : "#1e3a5f",
              fontWeight: "bold", fontSize: "13px", cursor: "pointer",
            }}>
            {label}
            {key === "mine" && myChallenges.length > 0 && (
              <span style={{ marginLeft: "6px", background: subTab === "mine" ? "rgba(255,255,255,0.3)" : "#dbeafe", borderRadius: "999px", padding: "1px 7px", fontSize: "11px" }}>
                {myChallenges.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* My Challenges */}
      {subTab === "mine" && (
        <div>
          {myChallenges.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
              <div style={{ fontSize: "36px", marginBottom: "10px" }}>🏆</div>
              <div style={{ fontSize: "15px", fontWeight: "bold", marginBottom: "6px" }}>No challenges yet</div>
              <div style={{ fontSize: "13px" }}>Browse public challenges or ask your coach to add you to one.</div>
              <button onClick={() => setSubTab("browse")}
                style={{ marginTop: "14px", padding: "10px 20px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer" }}>
                Browse Challenges
              </button>
            </div>
          )}
          {myChallenges.map((c) => (
            <ChallengeCard
              key={c.id}
              challenge={c}
              uid={uid}
              leaderboard={leaderboards[c.id]}
              expanded={expandedId === c.id}
              onExpand={() => handleExpand(c.id)}
              onLeave={() => handleLeave(c.id)}
              isLeaving={leavingId === c.id}
              manualEntry={manualEntry?.challengeId === c.id ? manualEntry : null}
              onManualEntryChange={(val) => setManualEntry(val ? { challengeId: c.id, ...val } : null)}
              onManualSubmit={() => handleManualSubmit(c.id)}
              submittingEntry={submittingEntry}
            />
          ))}
        </div>
      )}

      {/* Browse */}
      {subTab === "browse" && (
        <div>
          {browseChallenges.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
              <div style={{ fontSize: "36px", marginBottom: "10px" }}>🔍</div>
              <div style={{ fontSize: "15px", fontWeight: "bold" }}>No open challenges right now</div>
              <div style={{ fontSize: "13px", marginTop: "6px" }}>Check back soon or ask your coach to set one up.</div>
            </div>
          )}
          {browseChallenges.map((c) => (
            <div key={c.id} style={{ background: "#f8fbff", borderRadius: "16px", border: "2px solid #bfdbfe", marginBottom: "12px", padding: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "6px" }}>
                    <span style={{ fontSize: "20px" }}>{typeEmoji[c.type] || "🏆"}</span>
                    <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{c.title}</span>
                    <span style={{ padding: "2px 8px", borderRadius: "999px", background: statusColor[c.status] + "22", color: statusColor[c.status], fontSize: "11px", fontWeight: "bold" }}>
                      {c.status}
                    </span>
                  </div>
                  {c.description && <p style={{ margin: "0 0 6px", color: "#4b6b8a", fontSize: "13px" }}>{c.description}</p>}
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {typeLabel[c.type]}
                    {c.target && c.type !== "count" ? ` · Target: ${c.target} ${c.metric?.unit || ""}` : ""}
                    {" · "}{c.startDate} → {c.endDate}
                    {" · "}{(c.participants || []).length} joined
                    {c.prize && <span> · 🎁 {c.prize}</span>}
                  </div>
                </div>
                <button
                  onClick={() => handleJoin(c)}
                  disabled={joiningId === c.id}
                  style={{ padding: "9px 18px", borderRadius: "12px", border: "none", background: joiningId === c.id ? "#93c5fd" : "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "13px", cursor: "pointer", whiteSpace: "nowrap" }}>
                  {joiningId === c.id ? "Joining…" : "Join"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChallengeCard({ challenge: c, uid, leaderboard, expanded, onExpand, onLeave, isLeaving, manualEntry, onManualEntryChange, onManualSubmit, submittingEntry }) {
  const [leaveConfirm, setLeaveConfirm] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const myProgress = leaderboard?.find((p) => p.userId === uid);
  const myTotal = myProgress?.total || 0;

  // Challenge prefs (optimistic local state seeded from progress doc)
  const [prefs, setPrefs] = useState({
    pinnedToDashboard: myProgress?.pinnedToDashboard || false,
    notifInApp: myProgress?.notifInApp || false,
    notifPush: myProgress?.notifPush || false,
  });
  // Re-seed when leaderboard loads
  useEffect(() => {
    if (myProgress) {
      setPrefs({
        pinnedToDashboard: myProgress.pinnedToDashboard || false,
        notifInApp: myProgress.notifInApp || false,
        notifPush: myProgress.notifPush || false,
      });
    }
  }, [myProgress?.pinnedToDashboard, myProgress?.notifInApp, myProgress?.notifPush]);

  async function togglePref(key) {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    await updateChallengeProgressPrefs(c.id, uid, { [key]: next[key] });
  }
  const rank = leaderboard && myProgress
    ? leaderboard.filter((p) => (p.total || 0) > myTotal).length + 1
    : null;

  const isManual = c.metric?.trackingMode === "manual";
  const isCollective = c.type === "collective";
  const collectiveTotal = leaderboard ? leaderboard.reduce((sum, p) => sum + (p.total || 0), 0) : 0;
  const collectivePct = c.target ? Math.min(100, Math.round((collectiveTotal / c.target) * 100)) : 0;

  const isActive = c.status === "active";
  const isWinner = c.winnerId === uid;
  const collectiveWon = c.collectiveReached;

  return (
    <div style={{ background: "#f8fbff", borderRadius: "16px", border: `2px solid ${isWinner || collectiveWon ? "#fbbf24" : "#bfdbfe"}`, marginBottom: "12px", overflow: "hidden" }}>
      {(isWinner || collectiveWon) && (
        <div style={{ background: "#fef3c7", padding: "6px 16px", fontSize: "13px", fontWeight: "bold", color: "#92400e", textAlign: "center" }}>
          {collectiveWon ? "🎉 Goal reached! Well done everyone!" : "🏆 You won this challenge!"}
        </div>
      )}

      <div style={{ padding: "14px 16px", cursor: "pointer" }} onClick={onExpand}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
              <span style={{ fontSize: "18px" }}>{typeEmoji[c.type] || "🏆"}</span>
              <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{c.title}</span>
              <span style={{ padding: "2px 8px", borderRadius: "999px", background: statusColor[c.status] + "22", color: statusColor[c.status], fontSize: "11px", fontWeight: "bold" }}>
                {c.status}
              </span>
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              {c.startDate} → {c.endDate}
              {c.prize && <span> · 🎁 {c.prize}</span>}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontWeight: "bold", color: "#2563eb", fontSize: "20px" }}>{myTotal}</div>
            <div style={{ fontSize: "11px", color: "#6b7280" }}>{c.metric?.unit || "pts"}{rank ? ` · #${rank}` : ""}</div>
          </div>
        </div>

        {/* Collective progress bar */}
        {isCollective && c.target > 0 && (
          <div style={{ marginTop: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#4b6b8a", marginBottom: "4px" }}>
              <span>Group progress</span>
              <span>{collectiveTotal} / {c.target} {c.metric?.unit || ""} ({collectivePct}%)</span>
            </div>
            <div style={{ height: "8px", borderRadius: "999px", background: "#dbeafe", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${collectivePct}%`, background: collectiveWon ? "#16a34a" : "#2563eb", borderRadius: "999px", transition: "width 0.4s" }} />
            </div>
          </div>
        )}
      </div>

      {expanded && (
        <div style={{ borderTop: "1.5px solid #dbeafe", padding: "14px 16px", background: "#f0f9ff" }}>
          {/* Leaderboard */}
          {leaderboard && leaderboard.length > 0 ? (
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "8px" }}>
                {isCollective ? "Everyone's contributions" : "Leaderboard"}
              </div>
              {(() => {
                // Compute true rank numbers (ties share the same rank)
                const ranked = leaderboard.map((p, i, arr) => {
                  const trueRank = arr.filter((q) => (q.total || 0) > (p.total || 0)).length + 1;
                  return { ...p, trueRank };
                });
                // Find totals that appear more than once (ties)
                const tiedTotals = new Set(
                  ranked.filter((p, _, arr) => arr.filter((q) => q.trueRank === p.trueRank).length > 1).map((p) => p.trueRank)
                );
                return ranked.map((p) => {
                  const isMe = p.userId === uid;
                  const isTied = tiedTotals.has(p.trueRank) && !isCollective;
                  const isFirst = p.trueRank === 1 && !isCollective;
                  const pct = c.target && c.type !== "count" ? Math.min(100, Math.round(((p.total || 0) / c.target) * 100)) : null;
                  const bg = isMe && isTied ? "#fef3c7" : isMe ? "#dbeafe" : isTied ? "#fef9ec" : "transparent";
                  const border = isTied ? "1px solid #fbbf24" : "1px solid transparent";
                  return (
                    <div key={p.userId} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px", background: bg, border, borderRadius: "10px", padding: "6px 10px" }}>
                      {!isCollective && (
                        <span style={{ fontSize: "12px", width: "26px", flexShrink: 0, color: isFirst ? "#d97706" : isTied ? "#b45309" : "#6b7280", fontWeight: isFirst || isTied ? "bold" : "normal" }}>
                          #{p.trueRank}
                        </span>
                      )}
                      {p.userAvatar ? (
                        <img src={p.userAvatar} alt={p.userName} style={{ width: "30px", height: "30px", borderRadius: "999px", objectFit: "cover", flexShrink: 0, border: isFirst ? "2px solid #fbbf24" : "2px solid #bfdbfe" }} />
                      ) : (
                        <div style={{ width: "30px", height: "30px", borderRadius: "999px", background: "#dbeafe", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>👤</div>
                      )}
                      <span style={{ flex: 1, fontSize: "13px", fontWeight: isMe ? "bold" : "normal", color: "#1e3a5f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.userName || "User"}{isMe ? " (you)" : ""}
                        {isTied && <span style={{ marginLeft: "6px", fontSize: "10px", color: "#b45309", fontWeight: "bold" }}>TIE</span>}
                      </span>
                      <span style={{ fontWeight: "bold", color: "#2563eb", fontSize: "14px", flexShrink: 0 }}>
                        {p.total || 0}{c.metric?.unit ? ` ${c.metric.unit}` : ""}
                      </span>
                      {pct !== null && <span style={{ fontSize: "11px", color: "#6b7280", flexShrink: 0 }}>{pct}%</span>}
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <p style={{ color: "#6b7280", fontSize: "13px", margin: "0 0 12px" }}>No one has logged progress yet.</p>
          )}

          {/* Manual log button */}
          {isManual && isActive && (
            <div style={{ marginBottom: "12px" }}>
              {!showManual ? (
                <button onClick={() => { setShowManual(true); onManualEntryChange({ value: 1, note: "" }); }}
                  style={{ padding: "8px 16px", borderRadius: "10px", border: "2px solid #2563eb", background: "#eff6ff", color: "#2563eb", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                  + Log Progress
                </button>
              ) : (
                <div style={{ background: "#fff", borderRadius: "10px", padding: "12px", border: "1.5px solid #bfdbfe" }}>
                  <div style={{ display: "flex", gap: "10px", marginBottom: "8px", alignItems: "flex-end" }}>
                    <div>
                      <label style={{ fontSize: "11px", color: "#4b6b8a", display: "block", marginBottom: "4px" }}>Amount ({c.metric?.unit || "units"})</label>
                      <input type="number" min="1" value={manualEntry?.value ?? 1}
                        onChange={(e) => onManualEntryChange({ value: Number(e.target.value), note: manualEntry?.note || "" })}
                        style={{ width: "80px", padding: "7px 10px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "14px" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: "11px", color: "#4b6b8a", display: "block", marginBottom: "4px" }}>Note (optional)</label>
                      <input value={manualEntry?.note || ""}
                        onChange={(e) => onManualEntryChange({ value: manualEntry?.value ?? 1, note: e.target.value })}
                        style={{ width: "100%", padding: "7px 10px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "13px", boxSizing: "border-box" }}
                        placeholder="Optional note…" />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => { setShowManual(false); onManualEntryChange(null); }}
                      style={{ padding: "7px 14px", borderRadius: "8px", border: "1.5px solid #d1d5db", background: "#f9fafb", color: "#374151", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
                    <button onClick={async () => { await onManualSubmit(); setShowManual(false); }}
                      disabled={submittingEntry}
                      style={{ padding: "7px 14px", borderRadius: "8px", border: "none", background: submittingEntry ? "#93c5fd" : "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>
                      {submittingEntry ? "Saving…" : "Log it"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Challenge prefs */}
          <div style={{ borderTop: "1px solid #dbeafe", paddingTop: "12px", marginBottom: "12px" }}>
            <div style={{ fontWeight: "bold", color: "#4b6b8a", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>Challenge Settings</div>
            {[
              { key: "pinnedToDashboard", label: "📌 Show on dashboard" },
              { key: "notifInApp", label: "🔔 In-app: notify when opponent logs" },
              { key: "notifPush", label: "📱 Push: notify when opponent logs" },
            ].map(({ key, label }) => (
              <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "13px", color: "#1e3a5f" }}>{label}</span>
                <button
                  onClick={() => togglePref(key)}
                  style={{ width: "44px", height: "24px", borderRadius: "999px", border: "none", background: prefs[key] ? "#2563eb" : "#d1d5db", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: "3px", left: prefs[key] ? "23px" : "3px", width: "18px", height: "18px", borderRadius: "999px", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                </button>
              </div>
            ))}
          </div>

          {/* Leave */}
          {isActive && (
            leaveConfirm ? (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: "#dc2626" }}>Leave this challenge?</span>
                <button onClick={() => { setLeaveConfirm(false); onLeave(); }}
                  disabled={isLeaving}
                  style={{ padding: "5px 12px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fee2e2", color: "#dc2626", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>
                  {isLeaving ? "Leaving…" : "Yes, leave"}
                </button>
                <button onClick={() => setLeaveConfirm(false)}
                  style={{ padding: "5px 12px", borderRadius: "8px", border: "1px solid #d1d5db", background: "#f9fafb", color: "#374151", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>No</button>
              </div>
            ) : (
              <button onClick={() => setLeaveConfirm(true)}
                style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fff5f5", color: "#dc2626", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>
                Leave challenge
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
